/**
 * Task #252 — Cache-only thumbnail lookup for the quick-search palette.
 *
 * Reads project thumbnails (base64 data URLs) from the already-loaded
 * `['/api/tree/snapshot', …]` React Query cache. Does NOT trigger any
 * fetch — when the snapshot has not been loaded yet, every lookup
 * returns `null` and the palette renders its existing neutral
 * placeholder. See task-252.md for the architectural rationale (no
 * server-side payload bloat, no per-row network burst).
 */

import { useMemo, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import type { TreeSnapshot } from '@shared/projectTreeDto';

type SnapshotEnvelope = { snapshot?: TreeSnapshot } | undefined;

/**
 * Build a `projectId → thumbnail` Map from every cached tree snapshot
 * in the query client. Empty/missing thumbnails are skipped so the
 * lookup returns `null` for cache-misses AND for projects that simply
 * have no thumbnail set yet.
 */
function buildThumbnailMap(queryClient: QueryClient): Map<string, string> {
  const map = new Map<string, string>();
  const entries = queryClient.getQueriesData<SnapshotEnvelope>({
    predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === '/api/tree/snapshot',
  });
  for (const [, envelope] of entries) {
    const snapshot = envelope?.snapshot;
    if (!snapshot) continue;
    for (const year of snapshot.years || []) {
      for (const project of year.projects || []) {
        const thumb = project.thumbnail;
        if (thumb && typeof thumb === 'string' && thumb.length > 0) {
          // Last write wins — newer snapshots overwrite older ones,
          // which is what we want when the cache has both a stale and
          // a fresh entry for the same project.
          map.set(project.id, thumb);
        }
      }
    }
  }
  return map;
}

/**
 * React hook returning a stable `(projectId) => string | null` lookup
 * function. Re-builds the underlying Map only when the tree-snapshot
 * cache actually changes (subscribes to the query cache via
 * `useSyncExternalStore`, so we don't accidentally own/refetch the
 * snapshot query from this consumer).
 */
export function useProjectThumbnailLookup(): (projectId: string) => string | null {
  const queryClient = useQueryClient();

  // Track a monotonically-increasing version number that ticks every
  // time a tree-snapshot query updates. `useSyncExternalStore` uses
  // this as the snapshot value so React can short-circuit re-renders
  // when nothing relevant changed.
  const version = useSyncExternalStore(
    (notify) => {
      const cache = queryClient.getQueryCache();
      const unsubscribe = cache.subscribe((event) => {
        const key = event.query.queryKey;
        if (Array.isArray(key) && key[0] === '/api/tree/snapshot') {
          notify();
        }
      });
      return unsubscribe;
    },
    () => {
      // We don't actually need a real "snapshot value" — React only
      // compares it by `Object.is`. Use the cache's current size +
      // dataUpdatedAt sum as a cheap change-detection token.
      const entries = queryClient.getQueriesData<SnapshotEnvelope>({
        predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === '/api/tree/snapshot',
      });
      let token = 0;
      for (const [, envelope] of entries) {
        // Each envelope reference itself is stable across renders
        // until React Query swaps it; XORing the hash of references is
        // sufficient — but JS has no object hash, so fall back to a
        // count proxy via length of envelope keys.
        if (envelope && envelope.snapshot) {
          token += (envelope.snapshot.years?.length || 0) + 1;
        }
      }
      return token;
    },
    () => 0, // SSR — never used in this client-only palette.
  );

  const map = useMemo(
    () => buildThumbnailMap(queryClient),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, version],
  );

  return useMemo(
    () => (projectId: string) => map.get(projectId) ?? null,
    [map],
  );
}
